/* eslint-disable no-negated-condition */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-fallthrough */
/* eslint-disable complexity */
import React, { useState, useEffect, useContext, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { usePopper } from 'react-popper';

import { SelectProvider } from './select-context';
import { FormFieldContext } from '../form-field/form-field.context';
import { SelectAllOptions } from './select-all/select-all';
import { ReactComponent as HardwareIcKeyboardArrowDown24px } from '../../../assets/icons/hardware/ic_keyboard_arrow_down_24px.svg';
import { ReactComponent as NavigationIcClose24px } from '../../../assets/icons/navigation/ic_close_24px.svg';
import { SelectSearch } from './search/search';
import { MotifIconButton } from '../../button/icon-button/icon-button';
import { MotifOption } from './option/option';
import { canHandleOutside } from '../../../utils/click-outside.util';
import { DefaultNoResultsTemplate } from '../templates/no-results-template';

let nextUniqueId;
nextUniqueId = 0;

const REM = 14;
const OPTION_HEIGHT = 46 / REM;

const isObjEmpty = obj => Object.keys(obj).length === 0;

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {{clear: React.ReactNode, dropdown: React.ReactNode}} [icons]
 * @prop {string} [id]
 * @prop {boolean} [required]
 * @prop {string | string[] | {}} [value]
 * @prop {string} [placeholder]
 * @prop {number | string} [visibleOptions]
 * @prop {(x: any | any[]) => void} [onChange]
 * @prop {boolean} [multiple]
 * @prop {boolean} [showSelectAllButton]
 * @prop {boolean | (() => void)} [filter]
 * @prop {string} [searchPlaceholder]
 * @prop {boolean} [disabled]
 * @prop {string} [ariaLabelledBy]
 * @prop {{}} [triggerButtonProps]
 * @prop {React.ReactNode} [noResultsTemplate]
 * @prop {string} [className]
 * @prop {{deselect: string, select: string}} [i18n]
 */
/**
 * @param {Props} props
 */

const MotifSelect = (props) => {
  const {
    children,
    icons = {},
    id = '',
    required = false,
    value = '',
    placeholder = '',
    visibleOptions = 5,
    onChange = null,
    multiple = false,
    showSelectAllButton = false,
    filter = false,
    searchPlaceholder = 'Search',
    disabled = false,
    ariaLabelledBy = '',
    triggerButtonProps = {},
    noResultsTemplate: NoResultsTemplate = DefaultNoResultsTemplate,
    className = '',
    i18n = { deselect: 'Deselect All', select: 'Select All' },
    ...other
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [optionSelected, setOptionSelected] = useState(multiple ? [] : -1);
  const [optionCurrent, setOptionCurrent] = useState(-1);
  const [selectValue, setSelectValue] = useState(value || '');
  const [displayedValue, setDisplayedValue] = useState(multiple ? [] : '');
  const [activeDescendant, setActiveDescendant] = useState('');
  const [hasSelectedAllOptions, setHasSelectedAllOptions] = useState({
    selected: false,
    indeterminate: false
  });
  const [optionsChildrenSorted, setOptionsChildrenSorted] = useState(children);
  const [searchOption, setSearchOption] = useState('');
  const [hasSelectedMultipleOption, setHasSelectedMultipleOption] = useState(false);

  const selectWrapperRef = useRef();
  const selectTriggerRef = useRef();
  const wrapperOptionsRef = useRef();
  const selectSearchRef = useRef();
  const listBoxRef = useRef();

  const totalOptions = React.Children.count(optionsChildrenSorted);

  const formFieldContext = useContext(FormFieldContext);

  const { styles, attributes, forceUpdate } = usePopper(
    selectWrapperRef.current,
    wrapperOptionsRef.current,
    {
      placement: 'auto',
      modifiers: [
        { name: 'offset', options: { offset: [0, 0] } },
        {
          name: 'flip',
          options: {
            allowedAutoPlacements: ['top', 'bottom']
          }
        }
      ]
    }
  );

  const optionsChildren = React.Children.map(children, (child, index) => {
    const {
      props: { value: optionValue }
    } = child;

    const isSelected = Array.isArray(selectValue)
      ? selectValue.includes(optionValue)
      : selectValue === optionValue;

    return React.cloneElement(child, {
      isCurrentSelected: selectValue === optionValue,
      optionIndex: index,
      isSelected
    });
  });

  const getValueDetails = valueSelected => {
    if (multiple || Array.isArray(valueSelected)) {
      const valuesSelected = optionsChildren.reduce(
        (acc, { props: { value: val, children: child, label, optionIndex } }) => {
          if (valueSelected.includes(val)) {
            acc[0].push(typeof child === 'object' ? label : child);
            acc[1].push(optionIndex);
          }

          return acc;
        },
        [[], []]
      );

      setDisplayedValue(valuesSelected[0]);
      setOptionSelected(valuesSelected[1]);
    } else {
      const currentValue = optionsChildren.reduce(
        (acc, { props: { value: val, children: child, label, optionIndex } }) => {
          if (val === valueSelected) {
            acc.displayedValue = typeof child === 'object' ? label : child;
            acc.optionSelected = optionIndex;
            acc.optionCurrent = optionIndex;
          }

          return acc;
        },
        {}
      );
      setDisplayedValue(isObjEmpty(currentValue) ? '' : currentValue.displayedValue);
      setOptionSelected(isObjEmpty(currentValue) ? -1 : currentValue.optionSelected);
      setOptionCurrent(isObjEmpty(currentValue) ? -1 : currentValue.optionCurrent);
    }
  };

  useEffect(() => {
    if (!value) {
      setSelectValue('');
      setDisplayedValue(multiple ? [] : '');
    } else {
      setSelectValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (selectValue) {
      getValueDetails(selectValue);
    }

    setOptionsChildrenSorted(optionsChildren);

    formFieldContext.setDisabled(disabled);
    formFieldContext.setRequired(required);
    formFieldContext.setFormElementType('selectInput');
    formFieldContext.setHasPlaceholder(placeholder && placeholder.length > 0);
    id ? formFieldContext.setId(id) : formFieldContext.setId(`motif-select-${nextUniqueId++}`);

    document.addEventListener('click', clickOutside);

    return () => {
      document.removeEventListener('click', clickOutside);
    };
  }, []);

  useEffect(() => {
    formFieldContext.setValue(displayedValue);
    formFieldContext.setHasContent(
      displayedValue.length > 0 || Object.keys(displayedValue).length > 0
    );
  }, [displayedValue]);

  useEffect(() => {
    if (!hasSelectedMultipleOption) {
      setOptionsChildrenSorted(optionsChildren);
    }
    setHasSelectedMultipleOption(false);
  }, [children]);

  useEffect(() => {
    if (multiple) {
      const childrenSorted = optionsChildrenSorted.map(option => ({
        ...option,
        props: {
          ...option.props,
          isSelected: selectValue.includes(option.props.value)
        }
      }));

      setOptionsChildrenSorted(childrenSorted);
    }

    if (multiple && (showSelectAllButton || filter)) {
      const selectedOptions = optionsChildrenSorted.map(({ props: { value: val } }) => val);

      setHasSelectedAllOptions({
        selected: selectValue.length > 0 && selectedOptions.every(opt => selectValue.includes(opt)),
        indeterminate: selectedOptions.some(opt => selectValue.includes(opt))
      });
    }
    getValueDetails(selectValue);
  }, [selectValue]);

  const filterOptions = options => {
    const filteredOptions = options.filter(
      ({ props: { children: child, label, value: optionValue } }) => {
        if (typeof filter === 'function') {
          if (typeof optionValue === 'object') {
            return filter(searchOption, optionValue);
          }
        }

        const option = typeof child === 'object' ? label : child;

        return option.toLowerCase().includes(searchOption.toLowerCase());
      }
    );

    return React.Children.map(filteredOptions, (child, index) =>
      React.cloneElement(child, {
        optionIndex: index
      })
    );
  };

  const sortOptions = options => {
    const optionsSelected = options.filter(({ props: { isSelected } }) => isSelected);
    const optionsNonSelected = options.filter(({ props: { isSelected } }) => !isSelected);

    const optionsSorted = [...optionsSelected, ...optionsNonSelected];

    return React.Children.map(optionsSorted, (child, index) =>
      React.cloneElement(child, {
        optionIndex: index
      })
    );
  };

  useEffect(() => {
    if (isOpen) {
      setActiveDescendant(`${formFieldContext.id}-${optionCurrent}`);
    } else {
      setActiveDescendant('');

      if (searchOption && multiple) {
        const filteredOptions = filterOptions(optionsChildren);
        const sortedOptions = sortOptions(filteredOptions);
        setOptionsChildrenSorted(sortedOptions);
      }

      if (searchOption && !multiple) {
        const filteredOptions = filterOptions(optionsChildren);
        setOptionsChildrenSorted(filteredOptions);
      }

      if (!searchOption && multiple) {
        const sortedOptions = sortOptions(optionsChildren);
        setOptionsChildrenSorted(sortedOptions);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const filteredOptions = filterOptions(optionsChildren);

    if (multiple) {
      const optionsValue = filteredOptions.map(({ props: { value: val } }) => val);

      setHasSelectedAllOptions({
        selected: optionsValue.length > 0 && optionsValue.every(opt => selectValue.includes(opt)),
        indeterminate: optionsValue.some(opt => selectValue.includes(opt))
      });
    }

    setOptionsChildrenSorted(filteredOptions);
    setOptionCurrent(-1);
  }, [searchOption]);

  const clickOutside = event => {
    if (!isOpen && canHandleOutside(selectWrapperRef, event)) {
      setIsOpen(false);
    }
  };

  const navigateThroughOptions = event => {
    event.preventDefault();

    const ARROW_UP = 38;
    const ARROW_DOWN = 40;
    const key = event.keyCode;

    if (key === ARROW_UP && optionCurrent > 0) {
      setOptionCurrent(optionCurrent - 1);
    }

    if (key === ARROW_DOWN && optionCurrent < totalOptions - 1) {
      setOptionCurrent(optionCurrent + 1);
    }
  };

  const updateSingleSelectedOption = option => {
    const optionDisabled = optionsChildrenSorted[option].props.disabled;
    if (!optionDisabled) {
      const selectedValue = optionsChildrenSorted[option].props.value;
      const selectedDisplayedValue =
        typeof optionsChildrenSorted[option].props.children === 'object'
          ? optionsChildrenSorted[option].props.label
          : optionsChildrenSorted[option].props.children;

      setOptionSelected(option);
      setSelectValue(selectedValue);
      setDisplayedValue(selectedDisplayedValue);

      if (onChange) {
        onChange(selectedValue);
      }
    }
  };

  const updateMultipleSelectedOption = (option) => {
    const optionDisabled = optionsChildrenSorted[option].props.disabled;

    if (!optionDisabled) {
      const optionValue = optionsChildrenSorted[option].props.value;
      const selectedDisplayedValue =
        typeof optionsChildrenSorted[option].props.children === 'object'
          ? optionsChildrenSorted[option].props.label
          : optionsChildrenSorted[option].props.children;

      setSelectValue(prevState => {
        if (prevState.includes(optionValue)) {
          const filteredValues = prevState.filter(val => val !== optionValue);
          if (onChange) {
            onChange(filteredValues);
          }

          return filteredValues;
        }
        if (onChange) {
          onChange([...prevState, optionValue]);
        }

        return [...prevState, optionValue];
      });

      setDisplayedValue(prevState => {
        if (prevState.includes(selectedDisplayedValue)) {
          return prevState.filter(val => val !== selectedDisplayedValue);
        }

        return [...prevState, selectedDisplayedValue];
      });

      setHasSelectedMultipleOption(true);
    }
  };

  const handleKeyDown = event => {
    const ENTER = 13;
    const SPACE = 32;
    const ESCAPE = 27;
    const ARROW_UP = 38;
    const ARROW_DOWN = 40;
    const TAB = 9;

    const key = event.keyCode;

    switch (key) {
      case ARROW_DOWN:
      case ARROW_UP:
        if (isOpen) {
          navigateThroughOptions(event);
        }
        break;

      case ESCAPE:
        setIsOpen(false);
        selectTriggerRef.current.focus();
        break;

      case ENTER:
        if (searchOption !== '' && optionCurrent === -1 && optionsChildrenSorted.length > 0) {
          updateSingleSelectedOption(optionCurrent + 1);
          selectTriggerRef.current.focus();
        }

        if (optionCurrent !== -1 && optionsChildrenSorted.length > 0) {
          updateSingleSelectedOption(optionCurrent);
          selectTriggerRef.current.focus();
        }
      case SPACE:
        if (!(document.activeElement === selectSearchRef.current)) {
          event.preventDefault();

          setIsOpen(!isOpen);

          if (optionCurrent > -1) {
            updateSingleSelectedOption(optionCurrent);
          }

          selectTriggerRef.current.focus();
        }

        break;

      case TAB:
        if (isOpen) {
          setIsOpen(false);
        }
        break;

      default:
        break;
    }
  };

  const handleKeyDownMultiple = event => {
    const ENTER = 13;
    const SPACE = 32;
    const ESCAPE = 27;
    const ARROW_UP = 38;
    const ARROW_DOWN = 40;
    const TAB = 9;

    const key = event.keyCode;

    switch (key) {
      case ARROW_DOWN:
      case ARROW_UP:
        if (isOpen) {
          navigateThroughOptions(event);
        }
        break;

      case ESCAPE:
        setIsOpen(false);
        setOptionCurrent(-1);
        selectTriggerRef.current.focus();
        break;

      case ENTER:
        if (searchOption !== '' && optionCurrent === -1 && optionsChildrenSorted.length > 0) {
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          }
          updateMultipleSelectedOption(optionCurrent + 1);
        }

        if (filter) {
          if (optionCurrent > -1 && optionsChildrenSorted.length > 0) {
            event.preventDefault();
            if (!isOpen) {
              setIsOpen(true);
            }
            updateMultipleSelectedOption(optionCurrent);
          }
        }

      case SPACE:
        if (!(document.activeElement === selectSearchRef.current)) {
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          }

          if (optionCurrent > -1) {
            updateMultipleSelectedOption(optionCurrent);
          }
        }
        break;

      case TAB:
        if (isOpen) {
          setIsOpen(false);
        }
        break;

      default:
        break;
    }
  };

  const handleClickSelectAll = () => {
    if (selectValue.length) {
      setSelectValue([]);
      setDisplayedValue([]);
      setOptionSelected([]);
      setHasSelectedAllOptions({ selected: false, indeterminate: false });
    } else {
      const selectedOptions = optionsChildren.reduce(
        (
          acc,
          { props: { value: val, children: child, label, optionIndex, disabled: optionDisabled } }
        ) => {
          if (!optionDisabled) {
            const valueShowed = typeof child === 'object' ? label : child;
            acc.selectValue = acc.selectValue ? [...acc.selectValue, val] : [val];
            acc.displayedValue = acc.displayedValue
              ? [...acc.displayedValue, valueShowed]
              : [valueShowed];
            acc.optionSelected = acc.optionSelected
              ? [...acc.optionSelected, optionIndex]
              : [optionIndex];
          }

          return acc;
        },
        {}
      );
      setSelectValue([...selectValue, ...selectedOptions.selectValue]);
      setDisplayedValue([...displayedValue, ...selectedOptions.displayedValue]);
      setOptionSelected([...optionSelected, ...selectedOptions.optionSelected]);
      setHasSelectedAllOptions({ selected: true, indeterminate: false });
    }
  };

  const handleChangeSearch = event => {
    setSearchOption(event.target.value);
  };

  const handleClickSearchCheckbox = () => {
    const selectedOptions = optionsChildrenSorted.reduce(
      (
        acc,
        { props: { value: val, children: child, label, optionIndex, disabled: optionDisabled } }
      ) => {
        if (optionDisabled) {
          acc.disabledOptions = acc.disabledOptions ? [...acc.disabledOptions, val] : [val];
        } else {
          const valueShowed = typeof child === 'object' ? label : child;
          acc.selectValue = acc.selectValue ? [...acc.selectValue, val] : [val];
          acc.displayedValue = acc.displayedValue
            ? [...acc.displayedValue, valueShowed]
            : [valueShowed];
          acc.optionSelected = acc.optionSelected
            ? [...acc.optionSelected, optionIndex]
            : [optionIndex];
        }

        return acc;
      },
      {}
    );

    const prevValuesSelected =
      selectValue && selectValue.filter(val => !selectedOptions.selectValue.includes(val));

    const prevDisplayedSelected =
      displayedValue && displayedValue.filter(val => !selectedOptions.displayedValue.includes(val));

    if (!hasSelectedAllOptions.selected && hasSelectedAllOptions.indeterminate) {
      if (selectedOptions.disabledOptions && selectedOptions.disabledOptions.length > 0) {
        setSelectValue([...prevValuesSelected]);
        if (onChange) {
          onChange([...prevValuesSelected]);
        }
        setDisplayedValue([...prevDisplayedSelected]);
      } else {
        setSelectValue([...new Set([...selectValue, ...selectedOptions.selectValue])]);
        if (onChange) {
          onChange([...new Set([...selectValue, ...selectedOptions.selectValue])]);
        }
        setDisplayedValue([...new Set([...displayedValue, ...selectedOptions.displayedValue])]);
      }
    } else if (hasSelectedAllOptions.selected && hasSelectedAllOptions.indeterminate) {
      setSelectValue([...prevValuesSelected]);
      if (onChange) {
        onChange([...prevValuesSelected]);
      }
      setDisplayedValue([...prevDisplayedSelected]);
    } else {
      setSelectValue([...prevValuesSelected, ...selectedOptions.selectValue]);
      if (onChange) {
        onChange([...prevValuesSelected, ...selectedOptions.selectValue]);
      }
      setDisplayedValue([...prevDisplayedSelected, ...selectedOptions.displayedValue]);
    }

    setHasSelectedMultipleOption(true);
  };

  const handleCleanValue = event => {
    event.preventDefault();
    setSelectValue('');
    setDisplayedValue('');
    setOptionCurrent(-1);
    if (onChange) {
      onChange('');
    }
    selectTriggerRef.current.focus();
  };

  const handleCleanValueKeyDown = event => {
    const ENTER = 13;
    const SPACE = 32;

    const key = event.keyCode;

    switch (key) {
      case ENTER:
      case SPACE:
        handleCleanValue(event);
        break;

      default:
        break;
    }
  };

  const classes = classNames({
    'motif-select': true,
    'motif-select-open': isOpen,
    'motif-select-disabled': disabled,
    'motif-select-invalid': formFieldContext.invalid,
    [className]: true
  });

  return (
    <SelectProvider
      value={{
        id: formFieldContext.id,
        optionSelected,
        setOptionSelected,
        selectValue,
        setSelectValue,
        setDisplayedValue,
        isOpen,
        setIsOpen,
        selectTriggerRef,
        optionCurrent,
        setOptionCurrent,
        multiple,
        searchPlaceholder,
        searchOption,
        handleClickSelectAll,
        handleChangeSearch,
        hasSelectedAllOptions,
        handleClickSearchCheckbox,
        totalOptions,
        setHasSelectedMultipleOption,
        selectSearchRef,
        onChange,
        listBoxRef
      }}
    >
      <div
        {...other}
        id={formFieldContext.id}
        ref={selectWrapperRef}
        className={classes}
        onKeyDownCapture={multiple ? handleKeyDownMultiple : handleKeyDown}
        onFocus={() => formFieldContext.toggleFocus(true)}
        onBlur={() => formFieldContext.toggleFocus(false)}
        data-motif-select
      >
        <button
          ref={selectTriggerRef}
          role="combobox"
          disabled={disabled}
          className="motif-select-input"
          onClick={() => setIsOpen(!isOpen)}
          aria-labelledby={ariaLabelledBy}
          aria-controls={`${formFieldContext.id}-listbox`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-activedescendant={activeDescendant}
          {...triggerButtonProps}
        >
          {formFieldContext.isFocused && !formFieldContext.hasContent && placeholder}
          <div className="motif-select-input-text">
            {multiple ? displayedValue.filter(Boolean).join(', ') : displayedValue}
          </div>

          {!multiple && filter && displayedValue && displayedValue.length > 0 && (
            <MotifIconButton
              className="motif-select-clean-value"
              onClick={handleCleanValue}
              aria-label="Remove option selected"
              onKeyDown={handleCleanValueKeyDown}
            >
              {icons.clear || <NavigationIcClose24px />}
            </MotifIconButton>
          )}

          {icons.dropdown || (
            <HardwareIcKeyboardArrowDown24px className="motif-select-input-arrow" />
          )}
        </button>
        <div
          className="motif-select-wrapper-options"
          ref={wrapperOptionsRef}
          style={styles.popper}
          {...attributes.popper}
        >
          {multiple && showSelectAllButton && <SelectAllOptions i18n={i18n} />}
          {filter && <SelectSearch />}
          <div
            role="listbox"
            ref={listBoxRef}
            id={`${formFieldContext.id}-listbox`}
            tabIndex="-1"
            className="motif-select-options"
            aria-labelledby={ariaLabelledBy}
            style={{ maxHeight: `${visibleOptions * OPTION_HEIGHT}rem` }}
          >
            {optionsChildrenSorted.length > 0 ? (
              optionsChildrenSorted
            ) : (
              <NoResultsTemplate
                message="No results were found"
                options={optionsChildren}
                searchTerm={searchOption}
              />
            )}
          </div>
        </div>
      </div>
    </SelectProvider>
  );
};

MotifSelect.propTypes = {
  children: PropTypes.node,
  icons: PropTypes.shape({
    clear: PropTypes.node,
    dropdown: PropTypes.node
  }),
  id: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  placeholder: PropTypes.string,
  visibleOptions: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  multiple: PropTypes.bool,
  showSelectAllButton: PropTypes.bool,
  disabled: PropTypes.bool,
  filter: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  searchPlaceholder: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  triggerButtonProps: PropTypes.instanceOf(Object),
  noResultsTemplate: PropTypes.func,
  className: PropTypes.string,
  i18n: PropTypes.shape({
    deselect: PropTypes.string,
    select: PropTypes.string
  })
};

export { MotifSelect };
